import { useCallback, useEffect, useMemo, useState } from 'react'
import { BarChart3, ChevronDown, ChevronUp, RefreshCw, Search } from 'lucide-react'
import { getBookLogo } from '../lib/logoMaps'
import { normalizeLeagueCode } from '../lib/oddsNormalization'
import { propsSupabase as supabase } from '../lib/supabase'
import './PropsBoardWidget.css'

const LEAGUES = ['ALL', 'NBA', 'NFL', 'NHL', 'MLB', 'CBB', 'TENNIS']
const PROPS_PAST_WINDOW_HOURS = 12
const PROPS_FUTURE_WINDOW_HOURS = 96
const PROPS_BOOK_ORDER = ['DraftKings Pick6', 'PrizePicks', 'Underdog', 'Betr Picks']

function normalizePropsLeague(league) {
  const normalized = normalizeLeagueCode(league)
  if (normalized === 'NCAAB' || normalized === 'NCAAM' || normalized === 'NCAAW' || normalized === 'CBB') return 'CBB'
  return normalized
}

function comparePropsBooks(left, right) {
  const leftIndex = PROPS_BOOK_ORDER.findIndex((entry) => entry.toLowerCase() === String(left).toLowerCase())
  const rightIndex = PROPS_BOOK_ORDER.findIndex((entry) => entry.toLowerCase() === String(right).toLowerCase())
  if (leftIndex !== -1 || rightIndex !== -1) {
    if (leftIndex === -1) return 1
    if (rightIndex === -1) return -1
    if (leftIndex !== rightIndex) return leftIndex - rightIndex
  }
  return String(left).localeCompare(String(right))
}

function formatPrice(row) {
  if (row?.higher_american_odds || row?.lower_american_odds) {
    const over = row.higher_american_odds ? (String(row.higher_american_odds).startsWith('-') ? row.higher_american_odds : `+${row.higher_american_odds}`) : '—'
    const under = row.lower_american_odds ? (String(row.lower_american_odds).startsWith('-') ? row.lower_american_odds : `+${row.lower_american_odds}`) : '—'
    return `O ${over} / U ${under}`
  }
  if (row?.more_multiplier != null || row?.less_multiplier != null) {
    const over = row.more_multiplier != null ? `O x${row.more_multiplier}` : 'O —'
    const under = row.less_multiplier != null ? `U x${row.less_multiplier}` : 'U —'
    return `${over} / ${under}`
  }
  if (Array.isArray(row?.selection_options_normalized)) {
    return row.selection_options_normalized.join(' / ')
  }
  return 'Over / Under'
}

function buildGroups(rows, search, league) {
  const groupMap = new Map()
  const nowMs = Date.now()
  const minStartMs = nowMs - PROPS_PAST_WINDOW_HOURS * 3600000
  const maxStartMs = nowMs + PROPS_FUTURE_WINDOW_HOURS * 3600000

  rows.forEach((row) => {
    const normalizedLeague = normalizePropsLeague(row.league)
    if (league !== 'ALL' && normalizedLeague !== league) return

    const startMs = row.start_time ? new Date(row.start_time).getTime() : Number.NaN
    if (!Number.isFinite(startMs) || startMs <= minStartMs || startMs >= maxStartMs) return

    const haystack = [
      row.player_name,
      row.player_team,
      row.opponent_team,
      row.participants,
      row.stat_type,
      row.bookmaker,
    ].join(' ').toLowerCase()

    if (search && !haystack.includes(search.toLowerCase())) return

    if (!groupMap.has(row.projection_group_id)) {
      groupMap.set(row.projection_group_id, {
        projection_group_id: row.projection_group_id,
        canonical_id: row.canonical_id,
        league: normalizedLeague,
        start_time: row.start_time,
        participants: row.participants,
        away_team: row.away_team,
        home_team: row.home_team,
        player_name: row.player_name,
        player_team: row.player_team,
        opponent_team: row.opponent_team,
        stat_type: row.stat_type,
        lineGroups: new Map(),
        bookmakers: new Set(),
      })
    }

    const group = groupMap.get(row.projection_group_id)
    group.bookmakers.add(row.bookmaker)

    const lineKey = row.projection_canonical_id || `${row.projection_group_id}|${row.line_normalized || row.line || 'NA'}`
    if (!group.lineGroups.has(lineKey)) {
      group.lineGroups.set(lineKey, {
        projection_canonical_id: row.projection_canonical_id,
        line: row.line_normalized || String(row.line ?? ''),
        raw_line: row.line,
        byBook: {},
      })
    }

    group.lineGroups.get(lineKey).byBook[row.bookmaker] = row
  })

  return Array.from(groupMap.values()).map((group) => {
    const lines = Array.from(group.lineGroups.values()).map((lineGroup) => ({
      ...lineGroup,
      coverage: Object.keys(lineGroup.byBook).length,
    })).sort((left, right) => {
      if (left.coverage !== right.coverage) return right.coverage - left.coverage
      return Number(left.raw_line || 0) - Number(right.raw_line || 0)
    })

    return {
      ...group,
      bookmakers: Array.from(group.bookmakers).sort(comparePropsBooks),
      mainLine: lines[0] || null,
      altLines: lines.slice(1),
      lineCount: lines.length,
    }
  }).sort((left, right) => new Date(left.start_time || 0) - new Date(right.start_time || 0))
}

export default function PropsBoardWidget() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [source, setSource] = useState('generated feed')
  const [league, setLeague] = useState('ALL')
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState({})

  const fetchRows = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      if (supabase) {
        const { data, error: supabaseError } = await supabase
          .from('live_props')
          .select('*')
          .gt('start_time', new Date(Date.now() - 12 * 3600000).toISOString())
          .order('start_time')
          .limit(10000)

        if (!supabaseError && Array.isArray(data) && data.length > 0) {
          setRows(data)
          setSource('supabase')
          return
        }
      }

      const response = await fetch('/data/expansionProps.generated.json')
      if (!response.ok) throw new Error(`Props feed unavailable (${response.status})`)
      const data = await response.json()
      setRows(Array.isArray(data) ? data : [])
      setSource('generated feed')
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  const groups = useMemo(() => buildGroups(rows, search, league), [rows, search, league])
  const allBooks = useMemo(() => {
    const books = new Set()
    groups.forEach((group) => group.bookmakers.forEach((book) => books.add(book)))
    return Array.from(books).sort(comparePropsBooks)
  }, [groups])

  const toggle = (groupId) => {
    setExpanded((prev) => ({ ...prev, [groupId]: !prev[groupId] }))
  }

  return (
    <div className="widget pbw-widget">
      <div className="widget-header">
        <h3 className="widget-title">
          <BarChart3 size={16} />
          Props Board
          <span className="pbw-count">{groups.length} groups · {allBooks.length} books · {source}</span>
        </h3>
        <button className="widget-refresh-btn" onClick={fetchRows} title="Refresh">
          <RefreshCw size={14} className={loading ? 'pbw-spinner' : ''} />
        </button>
      </div>

      <div className="pbw-toolbar">
        <select className="pbw-select" value={league} onChange={(event) => setLeague(event.target.value)}>
          {LEAGUES.map((value) => <option key={value} value={value}>{value === 'ALL' ? 'All Sports' : value}</option>)}
        </select>
        <div className="pbw-search-wrap">
          <Search size={14} className="pbw-search-icon" />
          <input
            className="pbw-search"
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search player, team, stat, bookmaker"
          />
        </div>
      </div>

      {error && <div className="pbw-error">{error}</div>}

      {loading && rows.length === 0 ? (
        <div className="pbw-loading">Loading props board...</div>
      ) : groups.length === 0 ? (
        <div className="pbw-empty">No props available for current filters</div>
      ) : (
        <div className="pbw-groups">
          {groups.map((group) => {
            const open = Boolean(expanded[group.projection_group_id])
            const books = group.bookmakers
            return (
              <div key={group.projection_group_id} className="pbw-card">
                <div className="pbw-card-header" onClick={() => toggle(group.projection_group_id)}>
                  <div className="pbw-card-meta">
                    <div className="pbw-player">{group.player_name}</div>
                    <div className="pbw-stat">{group.stat_type} · {group.player_team} vs {group.opponent_team}</div>
                    <div className="pbw-game">{group.league} · {group.participants}</div>
                  </div>
                  <div className="pbw-card-right">
                    <span className="pbw-main-line">Main line {group.mainLine?.line || '—'}</span>
                    <span className="pbw-books">{books.length} books</span>
                    {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </div>
                </div>

                <div className="pbw-table-wrap">
                  <div className="pbw-table">
                    <div className="pbw-header">
                      <div className="pbw-line-col">Line</div>
                      {books.map((book) => (
                        <div key={book} className="pbw-book-col" title={book}>
                          {getBookLogo(book) ? <img src={getBookLogo(book)} alt="" className="pbw-book-logo" onError={(event) => { event.target.style.display = 'none' }} /> : null}
                          <span>{book.length > 10 ? `${book.slice(0, 9)}…` : book}</span>
                        </div>
                      ))}
                    </div>

                    {group.mainLine && (
                      <div className="pbw-row pbw-main-row">
                        <div className="pbw-line-col">{group.mainLine.line}</div>
                        {books.map((book) => (
                          <div key={book} className={`pbw-cell ${group.mainLine.byBook[book] ? '' : 'empty'}`}>
                            {group.mainLine.byBook[book] ? formatPrice(group.mainLine.byBook[book]) : '—'}
                          </div>
                        ))}
                      </div>
                    )}

                    {open && group.altLines.map((lineGroup) => (
                      <div key={lineGroup.projection_canonical_id || lineGroup.line} className="pbw-row">
                        <div className="pbw-line-col">{lineGroup.line}</div>
                        {books.map((book) => (
                          <div key={book} className={`pbw-cell ${lineGroup.byBook[book] ? '' : 'empty'}`}>
                            {lineGroup.byBook[book] ? formatPrice(lineGroup.byBook[book]) : '—'}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                {group.altLines.length > 0 && (
                  <button type="button" className="pbw-toggle" onClick={() => toggle(group.projection_group_id)}>
                    {open ? 'Hide' : 'Show'} {group.altLines.length} alternate line{group.altLines.length === 1 ? '' : 's'}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
